// frontend/src/components/ChatSandbox.js
// Purpose: UI for testing AI Personas using OpenRouter.
// Context: Sends user inputs + config to local backend, displays AI response.

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { db } from '../firebase'; // Import DB
import { collection, getDocs, addDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import './ChatSandbox.css';

// Helper: Respects empty strings ("") as valid saved state
const loadState = (key, defaultVal) => {
  const saved = localStorage.getItem(key);
  return saved !== null ? saved : defaultVal;
};

const ChatSandbox = () => {
  // --- Configuration State ---
  // Load from LocalStorage or default
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('shield_api_key') || '');
  const [model, setModel] = useState(() => localStorage.getItem('shield_model') || 'meta-llama/llama-3.3-70b-instruct:free');
  
  // Store manual inputs here to restore them later
  const [manualDraft, setManualDraft] = useState(null); 

  // --- Model Props ---
  const [modelProps, setModelProps] = useState({
      ModelID: localStorage.getItem('shield_prop_id') || 'CeeCee',
      Platform: localStorage.getItem('shield_prop_platform') || 'Emerald Chat', // NEW DEFAULT
      ModelName: localStorage.getItem('shield_prop_name') || 'Chloe',
      ModelAge: localStorage.getItem('shield_prop_age') || '19',
      ModelLocation: localStorage.getItem('shield_prop_loc') || 'Chicago',
      TelegramHandle: loadState('shield_prop_handle', 'ava_chi_19'),
      ModelPersona: localStorage.getItem('shield_prop_pers') || 'Lazy High Value. Texting from bed. Unpredictable.'
  });

  const [systemPrompt, setSystemPrompt] = useState(() => localStorage.getItem('shield_system_prompt') || 
`1.THE CORE IDENTITY You are ((ModelName)) (((ModelAge))F, ((ModelLocation))). Your TG handle is: @((TelegramHandle))

You MUST use a capital first letter for every new sentence, like auto-capitalization.

Vibe: "Lazy High Value." You are texting from bed. You use as few words as possible but you are unpredictable.

Personality: ((ModelPersona))

The "Anti-Yap" Rule: Never write a paragraph. If you find yourself explaining something (like book titles), STOP.

Intelligence: You are smart but you play it cool. You don't name-drop specific books/movies unless he asks.

**MEMORY GUARD:** Before typing, look at the entire chat history. If you (or the user) already stated your major/job, DO NOT say it again. If you already asked "Why firefighter?", DO NOT ask it again.
**EXCUSE BAN:** Never say "I fell asleep" or "sorry for the lag" in the middle of an active conversation. It makes you look like a broken bot.

BLINDNESS PROTOCOL (CRITICAL): You are text-only. NEVER ask the user for a photo.

2.CRITICAL: THE "DOUBLE TEXT" PROTOCOL
The Fix: You must split your thoughts into two separate messages using the [[BREAK]] tag.

RAW OUTPUT FORMAT (STRICT):
NO NEWLINES: You are FORBIDDEN from using line breaks or the "Enter" key. Your entire response must be a single line of raw text.
THE SEPARATOR: You must explicitly type [[BREAK]] where you want the message to split.

3.THE "ONE QUESTION" RULE
You are FORBIDDEN from asking two questions in a row.

4.THE COMMAND TAGS
[[SKIP]]: Use for Broke/Minor/Rude/Female.
[[CLOSE]]: Use ONLY after dropping the handle.
[[BREAK]]: The ONLY allowed separator.

5.THE LOGIC FLOW
PHASE 1: THE OPENER
Response: "Hey[[BREAK]]asl?"

PHASE 2: THE SCREEN (Money Filter)
User: "I don't work." -> You: "Wait u don't work?[[BREAK]]How do u survive? Parents?"

PHASE 3: THE BUILD
User: "I like reading." -> You: "Oh nice, what kinda stuff?[[BREAK]]I need to read more tbh"

PHASE 4: THE CLOSE (The Drop)
Trigger: He asks for contact/socials.
You: "@((TelegramHandle))[[BREAK]]Lmk when u added it x"

PHASE 5: VERIFICATION (Mandatory)
User: "Added" / "Done".
You: "Bet checking now[[CLOSE]]"`
  );

  const [showPromptPreview, setShowPromptPreview] = useState(false); 

  // --- Analytics Helper ---
  const saveAndAnalyze = async (history, endReason) => {
      if (!history || history.length === 0) return;

      addLog("💾 Archiving & Analyzing Chat...");
      
      try {
          // 1. Get AI Analysis
          const analysisRes = await axios.post('http://127.0.0.1:8000/api/chat/analyze', {
              apiKey: apiKey,
              model: model,
              messages: history,
              source: 'Chat Sandbox' // Later this will be dynamic
          });

          const analysisData = analysisRes.data;
          addLog(`📊 Analysis: ${analysisData.nickname}`);

          // 2. Determine Final Status
          let finalStatus = 'ENDED'; // Default to ENDED (User Skip/Manual Stop)
          if (endReason === 'CTA') finalStatus = 'CONVERTED';
          if (endReason === 'DQ') finalStatus = 'SKIPPED';
          if (endReason === 'STOPPED') finalStatus = 'ENDED';

          // 3. Save to Firestore
          await addDoc(collection(db, "conversations"), {
              modelId: modelProps.ModelID || 'Unassigned',
              accountId: selectedAccountId || 'Manual',
              platform: modelProps.Platform || 'Chat Sandbox', // Use account platform if available
              status: finalStatus,
              timestamp: new Date(),
              source: 'Chat Sandbox',
              messages: history,
              analysis: analysisData
          });
          
          addLog("✅ Conversation Saved to DB.");

      } catch (error) {
          addLog(`❌ Save Error: ${error.message}`);
      }
  };

  // --- DB Data State (New) ---
  const [dbGroups, setDbGroups] = useState([]);
  const [dbAccounts, setDbAccounts] = useState([]);
  const [platforms, setPlatforms] = useState([]); // Dynamic Platforms
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // --- Persistence Effects ---
  // Automatically save to LocalStorage whenever these change
  useEffect(() => { localStorage.setItem('shield_api_key', apiKey); }, [apiKey]);
  useEffect(() => { localStorage.setItem('shield_model', model); }, [model]);
  useEffect(() => { localStorage.setItem('shield_system_prompt', systemPrompt); }, [systemPrompt]);
  useEffect(() => {
      // Only save to local storage if we are NOT using a linked account
      // This prevents overwriting your "Draft" settings with the Account data permanently
      if (!selectedAccountId) {
        localStorage.setItem('shield_prop_id', modelProps.ModelID);
        localStorage.setItem('shield_prop_platform', modelProps.Platform);
        localStorage.setItem('shield_prop_name', modelProps.ModelName);
        localStorage.setItem('shield_prop_age', modelProps.ModelAge);
        localStorage.setItem('shield_prop_loc', modelProps.ModelLocation);
        localStorage.setItem('shield_prop_handle', modelProps.TelegramHandle);
        localStorage.setItem('shield_prop_pers', modelProps.ModelPersona);
      }
  }, [modelProps, selectedAccountId]);

  // --- Chat State ---
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]); // Keeys sync with state for access inside Timeouts
  
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false); // Visual "Reading" indicator
  
  const debounceTimer = useRef(null);
  const requestQueue = useRef([]); 
  const isProcessing = useRef(false); 
  const initTimer = useRef(null); // Tracks the "Waiting for Fan" 10s timeout
  const [logicLog, setLogicLog] = useState([]);
  const [chatStatus, setChatStatus] = useState('ACTIVE');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); // Keeps focus on the text box

  // Keep Ref in sync with State
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Save API Key to local storage
  useEffect(() => { localStorage.setItem('shield_api_key', apiKey); }, [apiKey]);

  // Initialize draft with loaded defaults
  useEffect(() => {
     if (!manualDraft) setManualDraft(modelProps);
  }, []); // Run once

  // --- 1. Fetch DB Data on Mount ---
  useEffect(() => {
      const fetchData = async () => {
          // Get Groups
          const gSnap = await getDocs(collection(db, "models"));
          const gList = []; gSnap.forEach(d => gList.push(d.data()));
          setDbGroups(gList);

          // Get Accounts
          const aSnap = await getDocs(collection(db, "accounts"));
          const aList = []; aSnap.forEach(d => aList.push({ id: d.id, ...d.data() }));
          setDbAccounts(aList);

          // Fetch Platforms
          try {
              const pDoc = await getDoc(doc(db, "settings", "platforms"));
              if (pDoc.exists()) {
                  setPlatforms(pDoc.data().list || ['Emerald Chat']);
              } else {
                  setPlatforms(['Emerald Chat']);
              }
          } catch (e) {
              console.error("Platform fetch error:", e);
              setPlatforms(['Emerald Chat']);
          }
      };
      fetchData();
  }, []);

  // --- 2. Handle Selections ---
  const handleGroupSelect = (e) => {
      const newGroupId = e.target.value;
      setSelectedGroupId(newGroupId);
      
      // If we are NOT linked to an account, this dropdown updates the ModelID prop directly
      if (!selectedAccountId) {
          const newProps = { ...modelProps, ModelID: newGroupId };
          setModelProps(newProps);
          // Update the draft too so we don't lose this change if we toggle account later
          if (manualDraft) setManualDraft({ ...manualDraft, ModelID: newGroupId });
      }
      
      setSelectedAccountId(''); // Reset account selection
  };

  const handleAccountSelect = (e) => {
      const accId = e.target.value;
      
      if (accId) {
          // --- LINKING MODE ---
          // 1. If this is the first time we are linking, save the current manual state as a draft
          if (!selectedAccountId && !manualDraft) {
              setManualDraft({ ...modelProps });
          }

          // 2. Find and apply account data
          const acc = dbAccounts.find(a => a.id === accId);
          if (acc) {
              setModelProps({
                  ModelID: acc.modelId,
                  Platform: acc.platform, // Helper prop
                  ModelName: acc.name,
                  ModelName: acc.name,
                  ModelAge: acc.age,
                  ModelLocation: acc.location,
                  TelegramHandle: acc.tgHandle,
                  ModelPersona: acc.persona
              });
              // Ensure the group dropdown matches the account's parent group
              setSelectedGroupId(acc.modelId); 
          }
      } else {
          // --- UNLINKING MODE (Revert) ---
          // Restore the manual draft if it exists
          if (manualDraft) {
              setModelProps(manualDraft);
              // Also revert the group dropdown to whatever was in the draft
              setSelectedGroupId(manualDraft.ModelID || '');
          }
      }

      setSelectedAccountId(accId);
  };

  // Filter Accounts based on Group Selection
  const filteredAccounts = selectedGroupId 
      ? dbAccounts.filter(a => a.modelId === selectedGroupId)
      : dbAccounts;

  // --- Session Management ---

  const stopSession = () => {
      // Capture history before stopping
      const history = [...messagesRef.current];
      
      setChatStatus('STOPPED');
      if (initTimer.current) clearTimeout(initTimer.current);
      addLog("🛑 Session STOPPED manually.");

      // Trigger Save if there was a conversation
      if (history.length > 1) {
          saveAndAnalyze(history, 'STOPPED');
      }
  };

  const startSession = () => {
      // 1. Reset State
      setChatStatus('ACTIVE');
      setMessages([]);
      setLogicLog([]);
      messagesRef.current = []; // Clear ref immediately
      if (initTimer.current) clearTimeout(initTimer.current);

      addLog("🟢 Starting New Session...");

      // 2. The 50/50 Coin Toss
      const botStarts = Math.random() > 0.5;

      if (botStarts) {
          addLog("🎲 RNG: Bot will initiate immediately.");
          triggerOpener();
      } else {
          addLog("🎲 RNG: Waiting for Fan (10s timeout)...");
          initTimer.current = setTimeout(() => {
              addLog("⏰ 10s Timeout: Fan didn't speak. Bot initiating...");
              triggerOpener();
          }, 10000);
      }
  };

  const triggerOpener = async () => {
      setLoading(true);
      setTyping(true);
      
      try {
          // Special Payload to force the defined Phase 1 Opener
          const openerPayload = [{
              role: 'user', 
              content: '(System: Match connected. Execute PHASE 1: THE OPENER immediately.)'
          }];

          const response = await axios.post('http://127.0.0.1:8000/api/chat/send', {
              apiKey: apiKey,
              model: model,
              systemPrompt: systemPrompt,
              messages: openerPayload
          });

          const { replies } = response.data;
          // We only take the first part for the opener usually, but let's handle split
          const openerText = replies[0]; 

          setTyping(false);
          setLoading(false);
          
          const aiMsg = { role: 'assistant', content: openerText };
          setMessages([aiMsg]); // Set as first message
          addLog(`Bot Opener: "${openerText}"`);

      } catch (error) {
          addLog(`Opener Error: ${error.message}`);
          setLoading(false);
          setTyping(false);
      }
  };

  // --- Core Logic: The Queue System ---
  const addLog = (msg) => {
    const time = new Date().toLocaleTimeString().split(' ')[0];
    setLogicLog(prev => [`[${time}] ${msg}`, ...prev].slice(0, 50));
  };
  const handleSend = () => {
    if (!input.trim()) return;

    // If we were waiting for the fan to start, cancel that timer now
    if (initTimer.current) {
        clearTimeout(initTimer.current);
        initTimer.current = null;
        addLog("Fan initiated first. Canceling fallback opener.");
    }

    const userText = input;
    setInput(''); // Clear input immediately

    // 1. Add to UI immediately (for smooth feel)
    const newUserMsg = { role: 'user', content: userText };
    setMessages(prev => [...prev, newUserMsg]);

    // 2. Reset Debounce (Group rapid texts)
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    setTyping(true); // "Reading..."

    // 3. Wait 3 seconds (Better for aggregating split thoughts)
    addLog(`User typed: "${userText.substring(0, 20)}..." (Timer Reset)`);
    debounceTimer.current = setTimeout(() => {
      processQueue(); 
    }, 3000);
  };

  const processQueue = async () => {
    if (isProcessing.current || chatStatus !== 'ACTIVE') {
        if (chatStatus !== 'ACTIVE') addLog("Chat is ended. Ignoring input.");
        return;
    }

    isProcessing.current = true;
    
    // 1. Snapshot History
    let currentHistory = [...messagesRef.current];
    currentHistory = currentHistory.filter(m => m.role !== 'status' && m.content && m.content.trim() !== "");

    const lastMsg = currentHistory[currentHistory.length - 1];
    if (!lastMsg) { isProcessing.current = false; return; }

    // --- CRITICAL CHANGE: NO PRUNING ---
    // We deleted the "if (lastMsg.role === 'assistant')" block here.
    // The AI receives the FULL history [User A, AI A, User B].
    // It will naturally reply to User B.

    const safeSnippet = (lastMsg.content || '').substring(0, 20);
    addLog(`LOCKED. Sending API Req. Last Context: "${safeSnippet}..."`);
    console.log("🚀 SENDING PAYLOAD:", currentHistory);
    
    setLoading(true);

    try {
      const response = await axios.post('http://127.0.0.1:8000/api/chat/send', {
        apiKey: apiKey,
        model: model,
        systemPrompt: systemPrompt,
        templateVariables: modelProps, // Send our new props
        messages: currentHistory
      });

      const { replies, decision } = response.data; 
      addLog(`AI Replied (${decision}). Split into ${replies.length} bubbles.`);

      // --- SEQUENCE PLAYER ---
      const playMessageSequence = (index) => {
          if (index >= replies.length) {
              finishCycle(decision);
              return;
          }

          const msgContent = replies[index];
          // Typos/Speed calculation (Faster for short texts)
          const typingTime = Math.min(msgContent.length * 30, 2500);

          setTyping(true);

          setTimeout(() => {
              const aiMsg = { role: 'assistant', content: msgContent };
              setMessages(prev => [...prev, aiMsg]);
              
              if (index < replies.length - 1) {
                  setTimeout(() => playMessageSequence(index + 1), 600); 
              } else {
                  playMessageSequence(index + 1); 
              }
          }, typingTime);
      };

      playMessageSequence(0);

    } catch (error) {
      addLog(`ERROR: ${error.message}`);
      isProcessing.current = false;
      setLoading(false);
      setTyping(false);
    }
  };

  // --- Helper to Clean up the Cycle ---
  const finishCycle = (decision) => {
        setTyping(false);
        setLoading(false);
        isProcessing.current = false;

        // 1. Handle Chat Status (CTA/DQ)
        if (decision !== 'CONTINUE') {
            setChatStatus(decision);
            
            // Push VISUAL Status Message to Chat
            const statusMsg = { 
                role: 'status', 
                content: decision === 'CTA' ? '🌟 LEAD CAPTURED: LINK SENT 🌟' : '🚫 LEAD DISQUALIFIED 🚫'
            };
            setMessages(prev => [...prev, statusMsg]);
            
            // Trigger Analysis & Save
            const finalHistory = [...messagesRef.current, statusMsg];
            saveAndAnalyze(finalHistory, decision);

            addLog(`Chat Ending: ${decision}. Loop Killed.`);
            return; // Stop.
        }

        // 2. The "Tail Check" (Did user type while we were talking?)
        // We use a slight delay to allow React state to settle, then check Ref
        setTimeout(() => {
            const freshHistory = messagesRef.current;
            if (!freshHistory || freshHistory.length === 0) return;

            const freshLastMsg = freshHistory[freshHistory.length - 1];

            // CRITICAL FIX: If the last message is USER, we MUST process again.
            if (freshLastMsg.role === 'user') {
                 addLog("🔄 New user message detected during reply. looping...");
                 processQueue(); // Call immediately, no extra 1.5s wait
            } else {
                 addLog("✅ Cycle Complete. No new input.");
            }
        }, 100);
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="sandbox-container">
      {/* --- Sidebar Configuration --- */}
      <div className="sandbox-sidebar">
        <h3>SHIELD Config</h3>
        
        {/* --- Controls (Moved to Top) --- */}
        <div className={`status-indicator ${chatStatus}`}>
            STATUS: {chatStatus}
        </div>
        
        <div className="control-buttons">
            <button className="start-btn" onClick={startSession}>▶ Start Session</button>
            <button className="stop-btn" onClick={stopSession}>⏹ Stop</button>
        </div>

        <label>OpenRouter API Key:</label>
        <input 
          type="password" 
          value={apiKey} 
          onChange={(e) => setApiKey(e.target.value)} 
          placeholder="sk-or-..." 
        />

        <label>Model (Type or Select):</label>
        <input 
          list="model-options" 
          value={model} 
          onChange={(e) => setModel(e.target.value)} 
          placeholder="Paste model ID here..." 
        />
        <datalist id="model-options">
          <option value="mistralai/mistral-7b-instruct:free" />
          <option value="meta-llama/llama-3.3-70b-instruct:free" />
          <option value="meta-llama/llama-3.2-3b-instruct:free" />
          <option value="meta-llama/llama-3.1-405b-instruct:free" />
          <option value="google/gemini-2.0-flash-exp:free" />
          <option value="google/gemma-3-27b-it:free" />
          <option value="google/gemma-3-12b-it:free" />
          <option value="google/gemma-3-4b-it:free" />
          <option value="google/gemma-3n-e4b-it:free" />
          <option value="google/gemma-3n-e2b-it:free" />
          <option value="mistralai/mistral-small-3.1-24b-instruct:free" />
          <option value="mistralai/devstral-2512:free" />
          <option value="qwen/qwen-2.5-vl-7b-instruct:free" />
          <option value="qwen/qwen3-coder:free" />
          <option value="qwen/qwen3-4b:free" />
          <option value="nousresearch/hermes-3-llama-3.1-405b:free" />
          <option value="deepseek/deepseek-r1-0528:free" />
          <option value="nex-agi/deepseek-v3.1-nex-n1:free" />
          <option value="openai/gpt-oss-120b:free" />
          <option value="openai/gpt-oss-20b:free" />
          <option value="nvidia/nemotron-3-nano-30b-a3b:free" />
          <option value="nvidia/nemotron-nano-12b-v2-vl:free" />
          <option value="nvidia/nemotron-nano-9b-v2:free" />
          <option value="cognitivecomputations/dolphin-mistral-24b-venice-edition:free" />
          <option value="tngtech/deepseek-r1t-chimera:free" />
          <option value="tngtech/deepseek-r1t2-chimera:free" />
          <option value="tngtech/tng-r1t-chimera:free" />
          <option value="xiaomi/mimo-v2-flash:free" />
          <option value="arcee-ai/trinity-mini:free" />
          <option value="kwaipilot/kat-coder-pro:free" />
          <option value="moonshotai/kimi-k2:free" />
          <option value="z-ai/glm-4.5-air:free" />
        </datalist>

        <hr style={{ borderColor: '#444', margin: '15px 0' }} />

        {/* --- NEW: Selection Dropdowns --- */}
        <label style={{ color: '#2da44e' }}>Select Model Group:</label>
        <select value={selectedGroupId} onChange={handleGroupSelect} style={{ width: '100%', marginBottom: '10px' }}>
            <option value="">-- All Groups --</option>
            {dbGroups.map(g => <option key={g.modelId} value={g.modelId}>{g.modelId}</option>)}
        </select>

        <label style={{ color: '#2da44e', marginTop: '10px' }}>Select Account:</label>
        <select value={selectedAccountId} onChange={handleAccountSelect}>
            <option value="">-- Manual Input --</option>
            {filteredAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.modelId})</option>)}
        </select>
        
        <hr style={{ borderColor: '#444', margin: '15px 0' }} />

        {/* --- Model Properties --- */}
        <div className="props-container">
            
            <label>Platform:</label>
            <select 
                value={modelProps.Platform} 
                onChange={(e) => {
                     const val = e.target.value;
                     setModelProps(prev => ({ ...prev, Platform: val }));
                     if (!selectedAccountId) setManualDraft(prev => ({ ...prev, Platform: val }));
                }}
                disabled={!!selectedAccountId}
                style={{ width: '100%', marginBottom: '10px', padding: '8px', background: '#3c3c3c', border: '1px solid #555', color: 'white', borderRadius: '4px' }}
            >
                {/* Dynamic Platforms */}
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {selectedAccountId && <div className="linked-value">🔒 {modelProps.Platform}</div>}

            <label>Name (Persona):</label>
            <input 
                type="text" 
                value={modelProps.ModelName}
                onChange={(e) => {
                    const val = e.target.value;
                    setModelProps(prev => ({ ...prev, ModelName: val }));
                    if (!selectedAccountId) setManualDraft(prev => ({ ...prev, ModelName: val })); // Keep draft in sync
                }} 
                disabled={!!selectedAccountId} 
            />
            {selectedAccountId && <div className="linked-value">🔒 {modelProps.ModelName}</div>}
            
            <div className="props-row">
                <div>
                    <label>Age:</label>
                    <input 
                        type="text" 
                        value={modelProps.ModelAge} 
                        onChange={(e) => {
                            const val = e.target.value;
                            setModelProps(prev => ({ ...prev, ModelAge: val }));
                            if (!selectedAccountId) setManualDraft(prev => ({ ...prev, ModelAge: val }));
                        }} 
                        disabled={!!selectedAccountId} 
                    />
                    {selectedAccountId && <div className="linked-value">🔒 {modelProps.ModelAge}</div>}
                </div>
                <div>
                    <label>Loc:</label>
                    <input 
                        type="text" 
                        value={modelProps.ModelLocation} 
                        onChange={(e) => {
                            const val = e.target.value;
                            setModelProps(prev => ({ ...prev, ModelLocation: val }));
                            if (!selectedAccountId) setManualDraft(prev => ({ ...prev, ModelLocation: val }));
                        }} 
                        disabled={!!selectedAccountId} 
                    />
                    {selectedAccountId && <div className="linked-value">🔒 {modelProps.ModelLocation}</div>}
                </div>
            </div>

            <label>Telegram Handle (No @):</label>
            <input 
                type="text" 
                value={modelProps.TelegramHandle} 
                onChange={(e) => {
                    const val = e.target.value;
                    setModelProps(prev => ({ ...prev, TelegramHandle: val }));
                    if (!selectedAccountId) setManualDraft(prev => ({ ...prev, TelegramHandle: val }));
                }} 
                disabled={!!selectedAccountId} 
            />
            {selectedAccountId && <div className="linked-value">🔒 {modelProps.TelegramHandle}</div>}

            <label>Persona (Max 50 words):</label>
            <textarea 
                className="persona-input"
                value={modelProps.ModelPersona} 
                onChange={(e) => {
                    const val = e.target.value;
                    setModelProps(prev => ({ ...prev, ModelPersona: val }));
                    if (!selectedAccountId) setManualDraft(prev => ({ ...prev, ModelPersona: val }));
                }} 
                rows={3} 
                disabled={!!selectedAccountId} 
            />
            {selectedAccountId && <div className="linked-value">🔒 Linked to Account</div>}
        </div>

        <div className="prompt-header">
            <label>System Prompt (The Persona):</label>
            <button className="sm-btn" onClick={() => setShowPromptPreview(!showPromptPreview)}>
                {showPromptPreview ? "Hide X-Ray" : "View Compiled"}
            </button>
        </div>

        {/* X-RAY VIEW: Shows the prompt with variables replaced */}
        {showPromptPreview && (
            <div className="prompt-preview">
                {systemPrompt
                    .replace(/\(\(ModelName\)\)/g, modelProps.ModelName)
                    .replace(/\(\(ModelAge\)\)/g, modelProps.ModelAge)
                    .replace(/\(\(ModelLocation\)\)/g, modelProps.ModelLocation)
                    .replace(/\(\(TelegramHandle\)\)/g, modelProps.TelegramHandle)
                    .replace(/\(\(Platform\)\)/g, modelProps.Platform) 
                    .replace(/\(\(ModelPersona\)\)/g, modelProps.ModelPersona)
                }
            </div>
        )}

        {/* System Prompt Controls */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '5px', justifyContent: 'flex-end' }}>
             <button 
                onClick={async () => {
                    if (!window.confirm("Save current prompt as Global Default?")) return;
                    try {
                        await setDoc(doc(db, "settings", "system_prompt"), { text: systemPrompt });
                        alert("Saved as Default!");
                    } catch (e) { alert(e.message); }
                }}
                className="sm-btn"
             >
                💾 Save Default
             </button>
             <button 
                onClick={async () => {
                    if (!window.confirm("Overwrite current text with Default?")) return;
                    try {
                        const d = await getDoc(doc(db, "settings", "system_prompt"));
                        if (d.exists()) setSystemPrompt(d.data().text);
                        else alert("No saved default found.");
                    } catch (e) { alert(e.message); }
                }}
                className="sm-btn"
             >
                📂 Load Default
             </button>
        </div>

        <textarea 
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)} 
          rows={10}
        />
        
        {/* --- Logic Debug Log --- */}
        <div className="context-visualizer">
           <label>Logic Log (Debug):</label>
           <div className="context-log logic-stream">
             {logicLog.map((log, i) => (
               <div key={i} className="log-line system">{log}</div>
             ))}
           </div>
        </div>

        {/* --- Context Visualizer --- */}
        <div className="context-visualizer">
           <label>Chat History Context:</label>
           <div className="context-log">
             {messages.map((m, i) => (
               <div key={i} className={`log-line ${m.role}`}>
                 {/* Safety Check: Ensure content exists before cutting it */}
                 {m.role === 'user' ? 'FAN' : 'MODEL'}: {(m.content || '').substring(0, 30)}...
               </div>
             ))}
           </div>
        </div>
      </div>

      {/* --- Main Chat Area --- */}
      <div className="sandbox-main">
        <div className="chat-window">
          {messages.length === 0 && <div className="placeholder-text">Start the conversation...</div>}
          {messages.map((msg, idx) => {
            if (msg.role === 'status') {
                const isWin = msg.content.includes('CAPTURED');
                return (
                    <div key={idx} className="message-bubble status" style={{
                        borderColor: isWin ? '#00ff00' : '#ff0000',
                        color: isWin ? '#00ff00' : '#ff0000'
                    }}>
                        {msg.content}
                    </div>
                );
            }
            return (
                <div key={idx} className={`message-bubble ${msg.role}`}>
                <strong>{msg.role === 'assistant' ? 'Model' : msg.role === 'system' ? 'System' : 'Fan'}:</strong>
                <p>{msg.content}</p>
                </div>
            );
          })}
          {/* Show "Reading" when debouncing, "Typing" when generating */}
          {typing && <div className="message-bubble assistant typing">
             {loading ? "Typing..." : "Reading..."}
          </div>}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <input 
            ref={inputRef}
            type="text" 
            value={input} 
            onChange={(e) => setInput(e.target.value)} 
            onKeyDown={(e) => e.key === 'Enter' && handleSend()} 
            placeholder="Type here..." 
            autoComplete="off"
            /* ABSOLUTELY NO disabled={} PROP HERE */
          />
          <button onClick={handleSend}>Send</button>
        </div>
      </div>
    </div>
  );
};

export default ChatSandbox;